import './style.scss';

import { Dialog } from 'primereact/dialog';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import dayjs from 'dayjs';
import { TIMEZONE_VN } from 'constant';
import { isAuthorized } from 'utils/func';
import { useMutation } from 'react-query';
import { deleteSummaryReportById } from 'apis/summaryReport';
import Confirm from '../../Confirm';

dayjs.extend(require('dayjs/plugin/timezone'));
dayjs.extend(require('dayjs/plugin/utc'));

function MidtermDocumentDialog({ midtermDocuments, proposalData, visible, setVisible, refetch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dialogConfirmDeleteRef = useRef();

  const { mutate: mutateDelete } = useMutation(deleteSummaryReportById);

  const user = JSON.parse(localStorage.getItem('user'));

  const formatName = ({ id, version }) => (
    <div>
      <Link
        to={`/project/${midtermDocuments?.[0]?.projectId}/submit-midterm-document/detail/${id}`}
      >
        {t('project.viewList.midtermDocumentDialog.versionText')} {version}
      </Link>
    </div>
  );

  const formatSubmitDate = ({ submittedAt }) => (
    <div>
      {submittedAt
        ? dayjs(submittedAt).tz(TIMEZONE_VN).format('DD/MM/YYYY')
        : t('project.viewList.midtermDocumentDialog.everSubmit')}
    </div>
  );

  const formatCouncilResult = ({ summaryReportRevises, council }) => {
    const isRevise = summaryReportRevises.findIndex((item) => item?.isRevise === true) !== -1;
    const qualified = council?.projectProposalCouncils?.[0]?.councilStatus;

    if (isRevise) {
      return <div>{t('project.viewList.midtermDocumentDialog.councilResult.revise')}</div>;
    }

    if (qualified) {
      return <div>{t(`project.viewList.midtermDocumentDialog.councilResult.${qualified}`)}</div>;
    }

    return <div>{t('project.viewList.midtermDocumentDialog.councilResult.everCouncilResult')}</div>;
  };

  const formatAction = ({ id, summaryReportRevises, submittedAt, council, version }) => {
    const reviseCount = summaryReportRevises?.length;
    const councilData = { ...council, id, version };

    if (
      !(
        proposalData?.owner?.id === user?.id ||
        isAuthorized(user?.id, proposalData?.projectProposalContacts)
      )
    ) {
      return null;
    }

    return (
      <div className="flex align-items-center justify-content-center flex-wrap border-round-lg gap-2">
        {!submittedAt && (
          <Link
            to={`/project/${midtermDocuments?.[0]?.projectId}/submit-midterm-document/edit/${id}`}
          >
            <Button
              severity="warning"
              className="flex align-items-center justify-content-center"
              tooltip={t('projectProposal.viewList.edit')}
              tooltipOptions={{ position: 'left', at: 'left top' }}
              style={{ width: '42px', height: '42px' }}
            >
              <i className="pi pi-pencil p-overlay-badge" />
            </Button>
          </Link>
        )}

        {reviseCount === 0 ? (
          <Button
            severity="secondary"
            className="flex align-items-center justify-content-center cursor-auto"
            tooltip={t('project.requestExplanation.noRequestRevise')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            style={{ width: '42px', height: '42px' }}
            icon="pi pi-file"
          />
        ) : (
          <Button
            severity="help"
            className="flex align-items-center justify-content-center pointer-events-auto"
            tooltip={t(
              `project.requestExplanation.${reviseCount > 0 ? 'requestRevise' : 'noRequestRevise'}`
            )}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            style={{ width: '42px', height: '42px' }}
            onClick={() => navigate(`/project/request/${id}`)}
          >
            <i className="pi pi-file p-overlay-badge">
              {reviseCount > 0 && <Badge value={reviseCount} severity="danger" />}
            </i>
          </Button>
        )}
        {!council && (
          <Button
            icon="pi pi-trash"
            severity="danger"
            tooltip={t('projectProposal.viewList.delete')}
            tooltipOptions={{ position: 'left', at: 'left top' }}
            onClick={() => {
              dialogConfirmDeleteRef.current.open({
                mode: 'delete',
                mutate: mutateDelete,
                councilData,
              });
            }}
            style={{ width: '42px', height: '42px' }}
          />
        )}
      </div>
    );
  };

  return (
    <Dialog
      header={t('project.viewList.midtermDocumentDialog.title')}
      visible={visible}
      onHide={() => setVisible(false)}
      draggable={false}
      style={{ maxWidth: '52rem' }}
      className="w-full"
    >
      <table className="w-full view-history-midterm-table">
        <thead>
          <tr>
            <th style={{ width: '4%' }} className="text-center">
              {t('project.viewList.midtermDocumentDialog.no')}
            </th>
            <th style={{ width: '33%' }} className="text-center">
              {t('project.viewList.midtermDocumentDialog.version')}
            </th>
            <th style={{ width: '15%' }} className="text-center">
              {t('project.viewList.midtermDocumentDialog.submitDate')}
            </th>
            <th style={{ width: '25%' }} className="text-center">
              {t('project.viewList.midtermDocumentDialog.councilResult.label')}
            </th>
            <th className="text-center">{t('project.viewList.midtermDocumentDialog.action')}</th>
          </tr>
        </thead>
        <tbody>
          {midtermDocuments?.length > 0 ? (
            midtermDocuments.map((midtermDocument, index) => (
              <tr key={midtermDocument.id}>
                <td className="text-center">{index + 1}</td>
                <td className="text-center">{formatName(midtermDocument)}</td>
                <td className="text-center">{formatSubmitDate(midtermDocument)}</td>
                <td className="text-center">{formatCouncilResult(midtermDocument)}</td>
                <td className="text-center">{formatAction(midtermDocument)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="text-center">
                {t('project.viewList.midtermDocumentDialog.noMidtermDocument')}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Confirm ref={dialogConfirmDeleteRef} refetch={refetch} setParentVisible={setVisible} />
    </Dialog>
  );
}

export default React.memo(MidtermDocumentDialog);

MidtermDocumentDialog.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  midtermDocuments: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  proposalData: PropTypes.array.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};
